import React, { useMemo } from "react";
import * as THREE from "three";
import { Edges } from "@react-three/drei";
import { computeOrientation } from "../transformations";
import { ElementCoordinateSystem } from "../common_types";
import { useStoreSelector } from "../../../../../../../../hooks/StoreHooks";
import { InstallationType } from "../../forms/Pergola_type";
import { a, useSpring } from "@react-spring/three";
import { LatoPergola } from "../Pergola";
import { VetrateType } from "../../../../../../../models/pergola_configuration";

interface GuidaProps {
  lunghezza: number;
  material: THREE.Texture;
  cs: ElementCoordinateSystem;
  slideOffset?: number;
  axis?: "x" | "y";
  direzione: string;
  isOpen: boolean;
  lunghezzaVetro: number;
}


interface VetroProps {
  lunghezza: number;
  material: THREE.Texture;
  cs: ElementCoordinateSystem;
  altezzaPilastro: number;
  slideOffset: number;
  axis: "x" | "y";
  isOpen: boolean;
  lunghezzaVetro: number;
  muro: number;
  direzione: string;
}

interface CompleteVetrataAndromedaProps {
  lunghezzaSoletta: number;
  larghezzaSoletta: number;
  altezzaPilastro: number;
  sezionePilastro: { larghezza: number; altezza: number };
  sezioneTrave: { larghezza: number; altezza: number };
  texture: THREE.Texture;
  lato: LatoPergola;
  isOpen?: boolean;
  needExtra: boolean;
  muro: number;
  segmentiLiberi: { start: number; end: number }[];
}

type Sezione = { larghezza: number; altezza: number };

const sezioneGuidaUp: Sezione = { larghezza: 0.04, altezza: 0.04 };
const sezioneGuidaBelow: Sezione = { larghezza: 0.043, altezza: 0.034 };
const sezioneVetro: Sezione = { larghezza: 0.01, altezza: 0.01 };

/**
 * Guida superiore animata
 */
const GuidaUp: React.FC<GuidaProps> = ({ lunghezza, material, cs, slideOffset = 0, axis = "y",lunghezzaVetro,isOpen, direzione }) => {
  const rotation = useMemo<[number, number, number]>(
    () => computeOrientation(cs.direction) as [number, number, number],
    [cs.direction]
  );

  const { offset, rotY } = useSpring({
    offset: slideOffset,
    rotY: slideOffset !== 0 ? rotation[1] + Math.PI / 2 : rotation[1],
    config: { mass: 3, tension: 60, friction: 40 },
  });
  const position = offset.to(o => [
      cs.org.x +
         (axis === "x"
          ? o
          : isOpen && slideOffset && direzione === "Sinistra"
          ? (lunghezzaVetro / 2) * (o / slideOffset)
          : isOpen && slideOffset &&direzione === "Destra"
          ? -(lunghezzaVetro / 2) * (o / slideOffset)
          : 0),
      cs.org.y +
        (axis === "y"
          ? o
          : isOpen && slideOffset && direzione === "Fronte"
          ? (lunghezzaVetro / 2) * (o / slideOffset)
          : isOpen && slideOffset && direzione === "Retro"
          ? -(lunghezzaVetro / 2) * (o / slideOffset)
          : 0),

      cs.org.z,
    ]) as unknown as [number, number, number] ;


  return (
    <a.mesh position={position} rotation-x={rotation[0]} rotation-y={rotY} rotation-z={rotation[2]}>
      <boxGeometry args={[sezioneGuidaUp.larghezza, sezioneGuidaUp.altezza, lunghezza]} />
      <meshStandardMaterial map={material} />
      <Edges color="#363636" />
    </a.mesh>
  );
};

/**
 * Guida inferiore animata
 */
const GuidaBelow: React.FC<GuidaProps> = ({ lunghezza, material, cs, slideOffset = 0, axis = "y",lunghezzaVetro,isOpen, direzione })=> {
  const rotation = useMemo<[number, number, number]>(
    () => computeOrientation(cs.direction) as [number, number, number],
    [cs.direction]
  );

  const { offset, rotY } = useSpring({
    offset: slideOffset,
    rotY: slideOffset !== 0 ? rotation[1] + Math.PI / 2 : rotation[1],
    config: { mass: 3, tension: 60, friction: 40 },
  });

  
  const position = offset.to(o => [
      cs.org.x +
         (axis === "x"
          ? o
          : isOpen && slideOffset && direzione === "Sinistra"
          ? (lunghezzaVetro / 2) * (o / slideOffset)
          : isOpen && slideOffset &&direzione === "Destra"
          ? -(lunghezzaVetro / 2) * (o / slideOffset)
          : 0),
      cs.org.y +
        (axis === "y"
          ? o
          : isOpen && slideOffset && direzione === "Fronte"
          ? (lunghezzaVetro / 2) * (o / slideOffset)
          : isOpen && slideOffset && direzione === "Retro"
          ? -(lunghezzaVetro / 2) * (o / slideOffset)
          : 0),

      cs.org.z,
    ]) as unknown as [number, number, number] ;

  return (
    <a.mesh position={position} rotation-x={rotation[0]} rotation-y={rotY} rotation-z={rotation[2]}>
      <boxGeometry args={[sezioneGuidaBelow.larghezza, sezioneGuidaBelow.altezza, lunghezza]} />
      <meshStandardMaterial map={material} />
      <Edges color="#363636" />
    </a.mesh>
  );
};

/**
 * Vetro animato
 */
const Vetro: React.FC<VetroProps> = ({ lunghezza, material, cs, altezzaPilastro, slideOffset, axis, isOpen,lunghezzaVetro,muro,direzione }) => {
  const rotation = useMemo<[number, number, number]>(
    () => computeOrientation(cs.direction) as [number, number, number],
    [cs.direction]
  );

  const { offset, rotY } = useSpring({
    offset: slideOffset,
    rotY: slideOffset !== 0 ? rotation[1] + Math.PI / 2 : rotation[1],
    config: { mass: 3, tension: 60, friction: 40 },
  });

  
  const position = offset.to(o => [
      cs.org.x +
         (axis === "x"
          ? o
          : isOpen && slideOffset && direzione === "Sinistra"
          ? (lunghezzaVetro / 2) * (o / slideOffset)
          : isOpen && slideOffset &&direzione === "Destra"
          ? -(lunghezzaVetro / 2) * (o / slideOffset)
          : 0),
      cs.org.y +
        (axis === "y"
          ? o
          : isOpen && slideOffset && direzione === "Fronte"
          ? (lunghezzaVetro / 2) * (o / slideOffset)
          : isOpen && slideOffset && direzione === "Retro"
          ? -(lunghezzaVetro / 2) * (o / slideOffset)
          : 0),

      cs.org.z + altezzaPilastro/2 + muro,
    ]) as unknown as [number, number, number] ;

  return (
    <a.mesh position={position} rotation-x={rotation[0]} rotation-y={rotY} rotation-z={rotation[2]}>
      <boxGeometry args={[sezioneVetro.larghezza, altezzaPilastro, lunghezza]} />
      <meshPhysicalMaterial
        transparent
        opacity={0.25}
        transmission={0.95}
        depthWrite={false}
        polygonOffset
        polygonOffsetFactor={-1}
        polygonOffsetUnits={-1}
      />
      <Edges color="#363636" />
    </a.mesh>
  );
};

/**
 * Funzione di calcolo parametri segmento
 */
const getSegmentoVetrataParams = (
  direzione: string,
  start: number,
  end: number,
  installazione: InstallationType | undefined,
  altezzaPilastro: number,
  larghezzaSoletta: number,
  lunghezzaSoletta: number,
  sezionePilastro: Sezione,
  sezioneTrave: Sezione,
  needExtra: boolean,
  muro: number,
  isOpen: boolean,
  lato: LatoPergola,
  verso?: string
) => {
  const lunghezzaSegmento = end - start;
  const centro = (start + end) / 2;
  const offsetExtra = needExtra ? sezioneTrave.altezza - 0.02 : 0;
  let maxVetrate = Math.round(lunghezzaSegmento / 0.7);

  const lunghezzaVetro =
    (lunghezzaSegmento +
        (
          (lato.direzione === "Sinistra" || lato.direzione === "Destra") && lunghezzaSoletta < 4
            ? 0
            : lato.segmento <= 1 && lato.direzione !== "Sinistra" && lato.direzione !== "Destra"
              ? 0.04
              : (lato.direzione === "Sinistra" || lato.direzione === "Destra") && lato.segmento > 1
                ? 0
                : sezionePilastro.larghezza + 0.02
        )
    ) /
    maxVetrate;
  
  let direction: [number, number, number] = [0, 0, 0];
  let xBase = 0,
    yBase = 0;

  switch (direzione) {
    case "Destra":
      direction = [0, 1, 0];
      xBase = larghezzaSoletta - sezioneTrave.larghezza / 2;
      yBase = centro;
      break;
    case "Sinistra":
      direction = [0, -1, 0];
      xBase = sezioneTrave.larghezza / 2;
      yBase = centro;
      break;
    case "Fronte":
      direction = [1, 0, 0];
      xBase = centro;
      yBase = sezioneTrave.larghezza / 2 + 0.02;
      break;
    case "Retro":
      direction = [-1, 0, 0];
      xBase = centro;
      yBase = lunghezzaSoletta - sezioneTrave.larghezza / 2 - 0.02;
      break;
  }

  const axis = Math.abs(direction[0]) === 1 ? ("x" as const) : ("y" as const);
  const dirAlongAxis = axis === "x" ? direction[0] : direction[1];

  const getOrg = (i: number) => {
    const startF = start - (lato.segmento <= 1 ? 0 : sezionePilastro.larghezza + 0.02);
    const offset = i * (lunghezzaVetro - (lato.segmento <= 1 ? 0.04 : 0));
    if (direzione === "Destra" || direzione === "Sinistra") {
      return { x: xBase, y: start + lunghezzaVetro / 2 + offset, z: 0 };
    } else {
      return { x: startF + lunghezzaVetro / 2 + offset, y: yBase, z: 0 };
    }
  };

  const getTargetOffset = (i: number): number => {
      if (!isOpen) return 0;

      // Thickness of each panel along the segment axis when folded 90°
      const stackedThickness = sezioneGuidaBelow.larghezza + 0.003;
      const originalPos = getOrg(i)[axis];

      // Determine stack direction: verso overrides default dirAlongAxis
      const stackTowardStart = verso !== undefined ? verso === "Destra" : dirAlongAxis > 0;
      const pilasterHalf = sezionePilastro.larghezza / 2;
      const targetPos = stackTowardStart
        ? start + pilasterHalf + stackedThickness * i + stackedThickness / 2
        : end - pilasterHalf - stackedThickness * i - stackedThickness / 2;

      return targetPos - originalPos;
    };


  return {
    lunghezzaSegmento,
    maxVetrate,
    lunghezzaVetro,
    direction,
    xBase,
    yBase,
    axis,
    dirAlongAxis,
    getOrg,
    getTargetOffset,
    guidaUpZ: altezzaPilastro - sezioneGuidaUp.larghezza/2 - offsetExtra,
    guidaBelowZ: muro + sezioneGuidaUp.larghezza/2,
  };
};

/**
 * COMPONENTE PRINCIPALE
 */
export const CompleteVetrataAndromeda: React.FC<CompleteVetrataAndromedaProps> = ({
  lunghezzaSoletta,
  larghezzaSoletta,
  altezzaPilastro,
  sezionePilastro,
  sezioneTrave,
  texture,
  lato,
  isOpen = false,
  needExtra,
  muro,
  segmentiLiberi,
}) => {
  const pergolaConfiguration = useStoreSelector(
    (store) => store.pergolaConfigurationBuilder
  );
  const installazione = pergolaConfiguration.configuration?.dimensioni.installazione;
  return (
    <>
      {segmentiLiberi.map((seg, segIdx) => {
        const verso = (lato.tipoLato.tipologia as VetrateType).verso;
        const p = getSegmentoVetrataParams(
          lato.direzione,
          seg.start,
          seg.end,
          installazione,
          altezzaPilastro,
          larghezzaSoletta,
          lunghezzaSoletta,
          sezionePilastro,
          sezioneTrave,
          needExtra,
          muro,
          isOpen,
          lato,
          verso
        );

        return (
          <group key={`${lato}-vetrata-segmento-${segIdx}`}>
            {Array.from({ length: p.maxVetrate }, (_, i) => {
              const org = p.getOrg(i);
              const offset = p.getTargetOffset(i);
              const altezzaV = needExtra  ? altezzaPilastro - muro - sezioneTrave.altezza  : altezzaPilastro - muro;
              const lunghezzaGuidaVetro = p.lunghezzaVetro - (lato.segmento <= 1 ? 0.04 : 0.01);
              return (
                <group key={i}>
                  <GuidaUp
                    lunghezza={lunghezzaGuidaVetro}
                    material={texture}
                    cs={{ org: { ...org, z: p.guidaUpZ }, direction: p.direction }}
                    slideOffset={offset}
                    axis={p.axis}
                    isOpen={isOpen}
                    direzione={lato.direzione}
                    lunghezzaVetro={p.lunghezzaVetro}
                  />

                  <GuidaBelow
                    lunghezza={lunghezzaGuidaVetro}
                    material={texture}
                    cs={{ org: { ...org, z: p.guidaBelowZ }, direction: p.direction }}
                    slideOffset={offset}
                    axis={p.axis}
                    isOpen={isOpen}
                    direzione={lato.direzione}
                    lunghezzaVetro={p.lunghezzaVetro}
                  />
                  <Vetro
                    lunghezza={lunghezzaGuidaVetro}
                    material={texture}
                    cs={{ org, direction: p.direction }}
                    altezzaPilastro={altezzaV}
                    slideOffset={offset}
                    axis={p.axis}
                    muro={muro}
                    direzione={lato.direzione}
                    isOpen={isOpen}
                    lunghezzaVetro={p.lunghezzaVetro}
                  />
                </group>
              );
            })}
          </group>
        );
      })}
    </>
  );
};
